import type { ReactNode } from 'react';

import { cx } from './cx';

/**
 * A state, in twelve pixels: a 1px border in `currentcolor` and a 3px radius.
 *
 * The border being `currentcolor` is what makes a pill one colour decision rather than two —
 * set the tone and the outline follows the ink. `soft` fills it with the matching wash
 * instead, for the pill that has to be read across a room rather than found in a table.
 */
export type Tone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'brand';

const INK: Record<Tone, string> = {
  neutral: 'text-ink-3',
  ok: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
  info: 'text-info',
  brand: 'text-brand',
};

const WASH: Record<Tone, string> = {
  neutral: 'bg-surface-2',
  ok: 'bg-ok-soft',
  warn: 'bg-warn-soft',
  danger: 'bg-danger-soft',
  info: 'bg-info-soft',
  brand: 'bg-brand-soft',
};

export function Pill({
  tone = 'neutral',
  soft,
  dot,
  title,
  className,
  children,
}: {
  tone?: Tone;
  soft?: boolean;
  /** The 7px disc a state pill carries when it is about something live. */
  dot?: boolean;
  /** The sentence a caps-short pill cannot fit, for a hover and a screen reader. */
  title?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center gap-[5px] text-[12px] leading-[1.4] py-px px-[7px] rounded-pill border whitespace-nowrap',
        INK[tone],
        soft ? cx(WASH[tone], 'border-transparent') : 'border-current',
        className,
      )}
    >
      {dot ? <span className="w-[7px] h-[7px] shrink-0 rounded-full bg-current" /> : null}
      {children}
    </span>
  );
}
